import {
  Shirt,
  CupSoda,
  ShoppingBag,
  Frame,
  Printer,
  PartyPopper,
  Footprints,
} from "lucide-react";

export const ACCENTS = {
  cyan: { hex: "#00E5FF", text: "text-brand-cyan", bg: "bg-brand-cyan", border: "border-brand-cyan", glow: "shadow-[0_0_60px_-15px_rgba(0,229,255,0.5)]" },
  magenta: { hex: "#FF007F", text: "text-brand-magenta", bg: "bg-brand-magenta", border: "border-brand-magenta", glow: "shadow-[0_0_60px_-15px_rgba(255,0,127,0.5)]" },
  yellow: { hex: "#FFE600", text: "text-brand-yellow", bg: "bg-brand-yellow", border: "border-brand-yellow", glow: "shadow-[0_0_60px_-15px_rgba(255,230,0,0.5)]" },
};

export const CATEGORIES = [
  {
    name: "Custom Apparel",
    slug: "custom-apparel",
    accent: "cyan",
    icon: Shirt,
    blurb: "DTF, sublimation & HTV garment printing built for daily wear.",
    subs: [
      { name: "DTF Printing", slug: "dtf-printing" },
      { name: "Sublimation Shirts", slug: "sublimation-shirts" },
      { name: "HTV / Vinyl Shirts", slug: "htv-vinyl-shirts" },
      { name: "Birthday / Memorial Shirts", slug: "birthday-memorial-shirts" },
      { name: "Company Shirts", slug: "company-shirts" },
      { name: "Jerseys", slug: "jerseys" },
    ],
  },
  {
    name: "Custom Drinkware",
    slug: "custom-drinkware",
    accent: "magenta",
    icon: CupSoda,
    blurb: "Mugs, tumblers and snow globes pressed in full color.",
    subs: [
      { name: "11oz Mug", slug: "11oz-mug" },
      { name: "12oz Wine Tumblers", slug: "12oz-wine-tumblers" },
      { name: "16oz Tumblers", slug: "16oz-tumblers" },
      { name: "Snow Globe Tumblers", slug: "snow-globe-tumblers" },
      { name: "Kids Cups", slug: "kids-cups" },
      { name: "40oz Tumblers", slug: "40oz-tumblers" },
    ],
  },
  {
    name: "Custom Bags & Accessories",
    slug: "custom-bags-accessories",
    accent: "yellow",
    icon: ShoppingBag,
    blurb: "Totes, hats, keychains and carry gear with your mark on it.",
    subs: [
      { name: "Tote Bags", slug: "tote-bags" },
      { name: "Drawstring Backpack", slug: "drawstring-backpack" },
      { name: "Pouches / Makeup Case", slug: "pouches-makeup-case" },
      { name: "Hats", slug: "hats" },
      { name: "Keychains", slug: "keychains" },
      { name: "Bottle Openers", slug: "bottle-openers" },
      { name: "Car Accessories", slug: "car-accessories" },
    ],
  },
  {
    name: "Photo & Gift Items",
    slug: "photo-gift-items",
    accent: "cyan",
    icon: Frame,
    blurb: "Photo slates, LED frames and keepsakes that hold the moment.",
    subs: [
      { name: "Aluminum Photo Slates", slug: "aluminum-photo-slates" },
      { name: "Acrylic LED Photo Frames", slug: "acrylic-led-photo-frames" },
      { name: "Fridge Magnets", slug: "fridge-magnets" },
      { name: "Mouse Pads", slug: "mouse-pads" },
      { name: "Buttons", slug: "buttons" },
      { name: "Photo Jewelry", slug: "photo-jewelry" },
    ],
  },
  {
    name: "Business Printing",
    slug: "business-printing",
    accent: "magenta",
    icon: Printer,
    blurb: "Cards, flyers, stickers and branded merch at production speed.",
    subs: [
      { name: "Business Cards", slug: "business-cards" },
      { name: "Flyers", slug: "flyers" },
      { name: "Stickers & Labels", slug: "stickers-labels" },
      { name: "Company/Branded Merchandise", slug: "company-branded-merchandise" },
    ],
  },
  {
    name: "Events & Occasions",
    slug: "events-occasions",
    accent: "yellow",
    icon: PartyPopper,
    blurb: "Birthdays, memorials, graduations and back-to-school drops.",
    subs: [
      { name: "Birthday Items", slug: "birthday-items" },
      { name: "Memorial Items", slug: "memorial-items" },
      { name: "Graduation Packages", slug: "graduation-fans" },
      { name: "School / Back to School Items", slug: "school-back-to-school-items" },
    ],
  },
  {
    name: "Customized Sneakers",
    slug: "customized-sneakers",
    accent: "cyan",
    icon: Footprints,
    blurb: "One-of-one customs, hand-finished in the studio.",
    subs: [],
  },
];

export const categoryBySlug = (slug) => CATEGORIES.find((c) => c.slug === slug);

// Category mockup imagery — optimized (w=800, q=60), uniform 4:3 crop in product grids
export const CATEGORY_IMAGES = {
  "custom-apparel":
    "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=800&q=60",
  "custom-drinkware":
    "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?auto=format&fit=crop&w=800&q=60",
  "custom-bags-accessories":
    "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&w=800&q=60",
  "photo-gift-items":
    "https://images.unsplash.com/photo-1513519245088-0e12902e5a38?auto=format&fit=crop&w=800&q=60",
  "business-printing":
    "https://images.unsplash.com/photo-1705039228815-a54991a30167?crop=entropy&cs=srgb&fm=jpg&w=800&q=60",
  "events-occasions":
    "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=800&q=60",
  "customized-sneakers":
    "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=800&q=60",
};

// Per-product mockup imagery — each product gets its own tile (uniform 4:3 crop, w=800 q=60)
const u = (id, extra = "") =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=800&q=60${extra}`;
const px = (id, extra = "") =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=800${extra}`;
const STICKERS_IMG =
  "https://images.unsplash.com/photo-1705039228815-a54991a30167?crop=entropy&cs=srgb&fm=jpg&w=800&q=60";
const FRAMES_IMG = u("1513519245088-0e12902e5a38");
const BALLOONS_IMG = u("1530103862676-de8c9debad1d");

export const PRODUCT_IMAGES = {
  // Apparel
  "dtf-custom-t-shirt": u("1618354691373-d851c5c3a990"),
  "sublimation-custom-t-shirt": u("1521572163474-6864f9cf17ab"),
  "birthday-memorial-shirt": u("1620799140408-edc6dcb6d633"),
  "htv-vinyl-custom-t-shirt": u("1648466104157-53bb5b191c72"),
  "custom-jersey": u("1523381210434-271e8be1f52b"),
  // Drinkware
  "11oz-custom-mug": u("1514228742587-6b1558fcca3d"),
  "12oz-wine-tumbler": u("1610701596007-11502861dcfa"),
  "16oz-sublimation-tumbler": px("1188649"),
  "kids-sublimation-cup": u("1610701596007-11502861dcfa", "&h=600"),
  "40oz-sublimation-tumbler": u("1602143407151-7111542de6e8"),
  "snow-globe-tumbler": px("1188649", "&h=600&fit=crop"),
  // Bags & Accessories
  "sublimation-tote-bag": u("1590874103328-eac38a683ce7"),
  "drawstring-backpack": u("1553062407-98eeb64c6a62"),
  "cosmetic-pencil-pouch": u("1590874103328-eac38a683ce7", "&h=600"),
  "custom-structured-hat": u("1588850561407-ed78c282e89b"),
  "acrylic-sublimation-keychain": `${STICKERS_IMG}&h=600`,
  "custom-bottle-opener": u("1602143407151-7111542de6e8", "&h=600"),
  "car-coaster-set": u("1610701596007-11502861dcfa", "&h=500"),
  "car-window-sign": u("1590874103328-eac38a683ce7", "&h=700"),
  // Photo & Gifts
  "led-acrylic-photo-frame": FRAMES_IMG,
  "aluminum-photo-slate-10-round": `${FRAMES_IMG}&h=600`,
  "aluminum-photo-print-11x14": `${FRAMES_IMG}&h=500`,
  "custom-fridge-magnet": `${STICKERS_IMG}&h=600`,
  "custom-mouse-pad": `${FRAMES_IMG}&h=700`,
  "custom-225-buttons": `${STICKERS_IMG}&h=500`,
  "rhinestone-photo-necklace": `${FRAMES_IMG}&h=550`,
  // Business Printing
  "custom-business-cards": u("1562564055-71e051d33c19"),
  "flyers-50": u("1504711434969-e33886168f5c"),
  "custom-stickers-labels": STICKERS_IMG,
  "company-branded-merch-pack": u("1553062407-98eeb64c6a62", "&h=600"),
  "business-launch-pack": u("1562564055-71e051d33c19", "&h=600"),
  // Events & Occasions
  "birthday-party-bundle": BALLOONS_IMG,
  "graduation-packages": `${BALLOONS_IMG}&h=650`,
  "graduation-fans-10pk": `${BALLOONS_IMG}&h=500`,
  "branded-party-cups": u("1610701596007-11502861dcfa", "&h=550"),
  "custom-chip-bags": `${STICKERS_IMG}&h=620`,
  "branded-water-labels": `${STICKERS_IMG}&h=550`,
  "capri-sun-labels": `${STICKERS_IMG}&h=650`,
  "cool-fruit-wraps": `${STICKERS_IMG}&h=700`,
  "mini-chocolate-wrappers": `${STICKERS_IMG}&h=750`,
  "hershey-bar-wrappers": `${STICKERS_IMG}&h=800`,
  "foam-board-welcome-sign": `${FRAMES_IMG}&h=650`,
  // Sneakers
  "custom-sneakers-basic-art": u("1542291026-7eec264c27ff"),
  "sneaker-refresh-repaint": px("374080"),
};

export const subName = (catSlug, subSlug) => {
  const cat = categoryBySlug(catSlug);
  const sub = cat?.subs.find((s) => s.slug === subSlug);
  return sub?.name || null;
};
